import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getApiBase } from "../lib/apiBase";

function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const apiBase = useMemo(() => getApiBase(), []);

  const canSubmit =
    email.trim() && password && confirmPassword && !isBusy;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password || !confirmPassword) {
      setError("Please fill out all fields.");
      setStatus("");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setStatus("");
      return;
    }

    setIsBusy(true);
    setError("");
    setStatus("Creating account...");

    try {
      const response = await fetch(`${apiBase}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Signup failed.");
      }

      setPassword("");
      setConfirmPassword("");
      setStatus("Account created.");
      navigate("/", { replace: true });
    } catch (err) {
      setStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-head">
        <p className="eyebrow">Get started</p>
        <h2>Create your account</h2>
        <p className="subtle">Set up your account to access meet day tools.</p>
      </div>

      <form className="form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Email address</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>
        <label className="field">
          <span>Password</span>
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Create a password"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="toggle-button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <svg
                className="eye-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M2.5 12s3.6-6.5 9.5-6.5S21.5 12 21.5 12 17.9 18.5 12 18.5 2.5 12 2.5 12Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                {showPassword ? (
                  <path
                    d="M4 20L20 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                ) : null}
              </svg>
            </button>
          </div>
        </label>
        <label className="field">
          <span>Confirm password</span>
          <div className="password-field">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Repeat your password"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="toggle-button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={
                showConfirmPassword ? "Hide confirm password" : "Show confirm password"
              }
            >
              <svg
                className="eye-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M2.5 12s3.6-6.5 9.5-6.5S21.5 12 21.5 12 17.9 18.5 12 18.5 2.5 12 2.5 12Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                {showConfirmPassword ? (
                  <path
                    d="M4 20L20 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                ) : null}
              </svg>
            </button>
          </div>
        </label>
        <button type="submit" disabled={!canSubmit}>
          {isBusy ? "Creating account..." : "Create account"}
        </button>
      </form>

      {status ? <div className="status">{status}</div> : null}
      {error ? <div className="error">{error}</div> : null}

      <div className="link-row">
        <span>Already have an account?</span>
        <Link className="link" to="/login">
          Log in
        </Link>
      </div>
    </div>
  );
}

export default Signup;
